import { Capacitor } from '@capacitor/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useIapState,
  IAP_PRODUCT_IDS,
} from 'features/payments/hooks/useIapState';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/capacitor';
import { isProductionEnvironment } from 'shared/envUtils';
import { useAuthState } from 'state/useAuthState';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import {
  getLatestOriginalTransactionId,
  syncAppPurchase,
} from './useIapInitializer.utils';

export const useIapInitializer = (): void => {
  const { setLoading } = useIapState();
  const { user } = useAuthState();
  const storeInitializedRef = useRef(false);
  const [iapTransactionId, setIapTransactionId] = useState<string | null>(null);

  const { mutate: syncIapPurchase } = useMutation({
    mutationFn: syncAppPurchase,
    retry: 1,
  });

  const requiresOriginalTransactionId =
    user?.supporterStatus?.requiresOriginalTransactionId;
  useEffect(() => {
    if (
      requiresOriginalTransactionId &&
      !iapTransactionId &&
      Capacitor.getPlatform() === 'ios'
    ) {
      Sentry.captureMessage('no iap original transactionId', {
        extra: { user },
      });
    }
    if (requiresOriginalTransactionId && iapTransactionId) {
      syncIapPurchase(iapTransactionId);
    }
    // Suppress ESLint warning about missing dependencies: 'user'.
    // This effect is intended to capture the user object at the time
    // 'requiresOriginalTransactionId' changes. Since the user is
    // expected to be logged in at this point, we don't need to
    // re-run the effect when other user properties change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iapTransactionId, requiresOriginalTransactionId, syncIapPurchase]);

  const onDeviceReady = useCallback(() => {
    if (storeInitializedRef.current) return;

    setLoading(true);
    const purchasePlatform = CdvPurchase.Platform.APPLE_APPSTORE;
    const recurringProductType = CdvPurchase.ProductType.PAID_SUBSCRIPTION;

    CdvPurchase.store.ready(() => {
      setLoading(false);
      storeInitializedRef.current = true;

      const originalTransactionId = getLatestOriginalTransactionId(
        CdvPurchase.store.localTransactions,
      );

      if (originalTransactionId) {
        setIapTransactionId(originalTransactionId);
      }
    });

    CdvPurchase.store.register([
      {
        type: recurringProductType,
        id: IAP_PRODUCT_IDS[MEMBERSHIP_PLANS.yearly],
        platform: purchasePlatform,
      },
      {
        type: recurringProductType,
        id: IAP_PRODUCT_IDS[MEMBERSHIP_PLANS.proYearly],
        platform: purchasePlatform,
      },
      {
        type: recurringProductType,
        id: IAP_PRODUCT_IDS[MEMBERSHIP_PLANS.proMonthly],
        platform: purchasePlatform,
      },
    ]);

    if (!isProductionEnvironment()) {
      CdvPurchase.store.verbosity = CdvPurchase.LogLevel.DEBUG;
    }

    CdvPurchase.store.initialize([purchasePlatform]);
  }, [setLoading]);

  useEffect(() => {
    const isIos = Capacitor.getPlatform() === 'ios';

    if (isIos) {
      document.addEventListener('deviceready', onDeviceReady);
    }

    return () => {
      if (isIos) {
        document.removeEventListener('deviceready', onDeviceReady);
      }
    };
  }, [onDeviceReady]);
};
